import { WindowRange } from '@pushly/models/lib/structs'
import { NotificationMatchQuery } from '@pushly/models/lib/structs/notif-conflicts-api/requests/notification-match-query'
import { StatusType } from '../enums/status-type'

/**
 * How far either side of a notification's send date a same-landing-URL send still counts as a
 * possible duplicate.
 *
 * The window is symmetric because a duplicate is a duplicate whichever send happens first: a
 * notification being edited can repeat one that already went out, or one scheduled for later.
 */
export const CONTENT_DUPLICATE_SEARCH_WINDOW_DAYS = 7

const MILLISECONDS_PER_DAY = 86400 * 1000

export interface IContentDuplicateSearchWindow {
    since: string
    through: string
}

export function buildContentDuplicateSearchWindow(sendDate: Date): IContentDuplicateSearchWindow {
    const windowMilliseconds = CONTENT_DUPLICATE_SEARCH_WINDOW_DAYS * MILLISECONDS_PER_DAY

    return {
        since: new Date(sendDate.getTime() - windowMilliseconds).toISOString(),
        through: new Date(sendDate.getTime() + windowMilliseconds).toISOString(),
    }
}

export interface IContentDuplicateMatchQueryOptions {
    queryId: string
    landingUrl?: string
    segmentIds: number[]
    sendDate: Date
    /** The notification being edited, so it is never reported as a duplicate of itself. */
    excludedNotificationId?: string
}

/**
 * The query that asks whether a notification's landing URL has already been sent, or is already
 * scheduled, to an overlapping audience around its send date. One match is enough to warn.
 */
export function buildContentDuplicateMatchQuery(options: IContentDuplicateMatchQueryOptions): NotificationMatchQuery {
    return NotificationMatchQuery.from({
        query_id: options.queryId,
        landing_url: options.landingUrl,
        // `landing_url_match_mode` is deliberately omitted so the API resolves the
        // mode from the domain's match config. Sending a mode here would override
        // whatever the client configured in domain settings.
        segment_ids: options.segmentIds,
        status: [StatusType.COMPLETED.name, StatusType.DELIVERING.name, StatusType.SCHEDULED.name] as any,
        notification_id: options.excludedNotificationId,
        window_range: WindowRange.from(buildContentDuplicateSearchWindow(options.sendDate)),
        limit: 1,
    })
}
